import React, { useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import Modal from './modal';
import PlansModal from './plans-modal';
import { Iframe } from '../editor';
import GmailImage from '../../../images/gmail.png';
import { Button, ButtonLink, ErrorBanner, P } from '../../../lib/ui';
import { trackProductEvent, trackLimitHit, trackUpgradeCtaClicked, setUpgradeSource } from '../../analytics';
import { PreviewClientIcon } from '../previews/client-logo';
import { PREVIEW_CLIENT_SECTIONS } from '../../email/preview-client-catalog';

/**
 * Share-page Gmail chrome. Iframe offsets in `.desktop-preview__iframe` are
 * pixels tuned for this width (`images/gmail.png` is 1128×972).
 * Shown at 80% of the width that would fill the paywall.
 */
const GMAIL_FRAME_WIDTH = 800;
const GMAIL_FRAME_HEIGHT = (972 * GMAIL_FRAME_WIDTH) / 1128;
const GMAIL_FRAME_SCALE = 0.8;

function withLockedDocument(html = '') {
  if (/<html[\s>]/i.test(html)) return html;
  return `<!doctype html><html><body>${html}</body></html>`;
}

function GmailFramePreview({
  html,
  css,
  title,
}: {
  html?: string;
  css?: string;
  title?: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(GMAIL_FRAME_SCALE);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const width = el.clientWidth;
      if (width <= 0) return;
      const fit = Math.min(1, width / GMAIL_FRAME_WIDTH);
      setScale(fit * GMAIL_FRAME_SCALE);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="mt-4 w-full">
      <div
        className="mx-auto overflow-hidden"
        style={{
          width: GMAIL_FRAME_WIDTH * scale,
          height: GMAIL_FRAME_HEIGHT * scale,
        }}>
        <div
          className="desktop-preview"
          style={{
            width: GMAIL_FRAME_WIDTH,
            maxWidth: GMAIL_FRAME_WIDTH,
            height: 'auto',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}>
          <img src={GmailImage} alt="" className="desktop-preview__client" />
          <Iframe
            onlyFrame
            src={withLockedDocument(html || '')}
            css={`${css || ''}\nhtml, body { overflow: hidden; }`}
            sandbox=""
            scrolling="no"
            id="free-live-preview-frame"
            frameBorder="0"
            className="desktop-preview__iframe"
            title={title || 'Template preview'}
          />
        </div>
      </div>
    </div>
  );
}

/** Max `setTimeout` delay (32-bit); `Infinity` overflows and dismisses almost immediately in react-hot-toast. */
const STICKY_TOAST_DURATION_MS = 2147483647;

const CREATE_PREVIEW = gql`
  mutation CreatePreview(
    $templateId: ID!
    $templateName: String!
    $rawTemplate: String!
    $css: String
    $subject: String!
    $clients: [String!]!
  ) {
    createPreview(
      input: {
        templateId: $templateId
        templateName: $templateName
        rawTemplate: $rawTemplate
        css: $css
        subject: $subject
        clients: $clients
      }
    ) {
      id
      status
      createdAt
    }
  }
`;

const ClientCheckbox = ({ client, checked, onChange, showDivider = true }) => {
  return (
    <label
      htmlFor={`preview-client-${client.id}`}
      className={`flex cursor-pointer items-center gap-2.5 px-5 py-2 transition-colors hover:bg-neutral-50 ${
        showDivider ? 'border-b border-subtle' : ''
      }`}>
      <input
        id={`preview-client-${client.id}`}
        type="checkbox"
        checked={checked}
        onChange={() => onChange(client.id)}
        className="sr-only"
      />
      <span
        className={`relative flex h-3.5 w-3.5 flex-none items-center justify-center rounded-[3px] border ${
          checked
            ? 'border-blue-dark bg-blue-dark'
            : 'border-neutral-300 bg-surface'
        }`}
        aria-hidden="true">
        {checked && (
          <svg
            className="h-2.5 w-2.5 text-white"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2.5 6.5L5 9L9.5 3.5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <PreviewClientIcon
        icon={client.icon}
        className={
          client.icon === 'ios' ? 'h-4 w-4 flex-none' : 'h-3.5 w-3.5 flex-none'
        }
      />
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-neutral-900">
        {client.label}
      </span>
      <span className="flex-none text-xs text-neutral-500">{client.subtitle}</span>
    </label>
  );
};

function LiveClientPreviewsModal({ toggle, isOpen, data, account }) {
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [createPreview, { loading: isSubmitting }] = useMutation(CREATE_PREVIEW);
  const limitHitTrackedRef = React.useRef(false);
  const selectedClientSet = useMemo(() => new Set(selectedClientIds), [selectedClientIds]);
  const clientSectionById = useMemo(
    () =>
      Object.fromEntries(
        PREVIEW_CLIENT_SECTIONS.flatMap((section) =>
          section.clients.map((client) => [client.id, section.key])
        )
      ),
    []
  );

  const maxLivePreviewCredits = account?.maxLivePreviewCredits ?? 0;
  const usedLivePreviewCredits = account?.livePreviewCount ?? 0;
  const remainingCredits =
    maxLivePreviewCredits === -1
      ? -1
      : Math.max(maxLivePreviewCredits - usedLivePreviewCredits, 0);
  const selectedCount = selectedClientIds.length;
  const isFreePlan = maxLivePreviewCredits === 0;
  const plan = account?.plan || (isFreePlan ? 'FREE' : undefined);
  const exceedsCredits =
    maxLivePreviewCredits !== -1 && selectedCount > remainingCredits;
  const canGenerate =
    selectedCount > 0 &&
    !isFreePlan &&
    !exceedsCredits &&
    !isSubmitting;
  const baseTrackingProps = {
    template_id: data?.id || '',
    remaining_credits: remainingCredits,
    is_free_plan: isFreePlan,
  };

  React.useEffect(() => {
    if (!isOpen) {
      limitHitTrackedRef.current = false;
      setShowPlansModal(false);
      return;
    }
    if ((isFreePlan || exceedsCredits) && !limitHitTrackedRef.current) {
      limitHitTrackedRef.current = true;
      trackLimitHit({
        limit_type: 'live_preview_credits',
        plan,
        source: 'live_previews_modal',
      });
    }
  }, [isOpen, isFreePlan, exceedsCredits, plan]);

  const onUpgradeClick = () => {
    trackProductEvent('Live Previews Upgrade Clicked', {
      ...baseTrackingProps,
      source: 'live_previews_modal',
      selected_count: selectedCount,
    });
    trackUpgradeCtaClicked({
      source: 'live_previews_modal',
      limit_type: 'live_preview_credits',
      plan,
    });
    setUpgradeSource('live_previews_modal', 'live_preview_credits');
    setShowPlansModal(true);
  };

  const closePlansAndReturnToEditor = () => {
    setShowPlansModal(false);
    toggle();
  };
  const toggleClientSelection = (clientId) => {
    setSelectedClientIds((current) => {
      const isSelected = current.includes(clientId);
      const nextClientIds = isSelected
        ? current.filter((id) => id !== clientId)
        : [...current, clientId];

      trackProductEvent('Live Preview Client Toggled', {
        ...baseTrackingProps,
        client_id: clientId,
        selected: !isSelected,
        selected_count: nextClientIds.length,
        section_key: clientSectionById[clientId] || '',
      });

      return nextClientIds;
    });
  };

  const selectAllClients = () => {
    const nextClientIds = PREVIEW_CLIENT_SECTIONS.flatMap((section) =>
      section.clients.map((client) => client.id)
    );

    trackProductEvent('Live Preview Select All Clicked', {
      ...baseTrackingProps,
      selected_count: nextClientIds.length,
      selected_client_ids: nextClientIds.join(','),
    });

    setSelectedClientIds(nextClientIds);
  };

  const clearSelectedClients = () => {
    trackProductEvent('Live Preview Select None Clicked', {
      ...baseTrackingProps,
      selected_count: 0,
    });

    setSelectedClientIds([]);
  };

  const onGenerate = async () => {
    setFormError(null);
    let loadingToastId: string | undefined;

    trackProductEvent('Live Previews Generate Clicked', {
      ...baseTrackingProps,
      selected_count: selectedClientIds.length,
      selected_client_ids: selectedClientIds.join(','),
    });

    try {
      const promise = createPreview({
        variables: {
          templateId: data?.id,
          templateName: data?.name,
          rawTemplate: data?.rawTemplate,
          css: data?.css,
          subject: `Live Client Previews - ${data?.name || 'Untitled'}`,
          clients: selectedClientIds,
        },
      });
      loadingToastId = toast.loading('Generating live previews...');

      const result = await promise;
      const previewId = result?.data?.createPreview?.id;

      if (!previewId) {
        throw new Error('Preview was created without a results page id.');
      }

      toast.dismiss(loadingToastId);
      toggle();
      setSelectedClientIds([]);

      window.setTimeout(() => {
        toast(
          (t) => (
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-green-500 text-xs font-semibold text-white">
                ✓
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-900">Live previews are ready.</p>
                <p className="mt-1 text-sm text-neutral-500">
                  Open them in a new tab when you&apos;re ready.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <ButtonLink
                    href={`/preview/${previewId}`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Open previews
                  </ButtonLink>
                  <button
                    type="button"
                    className="text-sm font-medium text-neutral-500 hover:text-neutral-700"
                    onClick={() => toast.dismiss(t.id)}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ),
          {
            id: `live-previews-ready-${previewId}`,
            duration: STICKY_TOAST_DURATION_MS,
            icon: null,
          }
        );
      }, 0);
    } catch (error) {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      const message = error.message || 'Unable to generate live previews.';
      setFormError(message);
      if (
        message.toLowerCase().includes('live preview') ||
        message.toLowerCase().includes('please upgrade')
      ) {
        trackLimitHit({
          limit_type: 'live_preview_credits',
          plan,
          source: 'live_previews_modal',
        });
      }
      toast.error('Unable to generate live previews.');
    }
  };

  const renderCategorySection = (section, isLastSection) => {
    const clients = section.clients;

    if (!clients.length) {
      return null;
    }

    return (
      <div key={section.key}>
        <div className="border-b border-subtle bg-neutral-50 px-5 py-1.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.05em] text-neutral-400">
            {section.title}
          </h4>
        </div>
        <div>
          {clients.map((client, clientIndex) => (
            <ClientCheckbox
              key={client.id}
              client={client}
              checked={selectedClientSet.has(client.id)}
              onChange={toggleClientSelection}
              showDivider={
                clientIndex < clients.length - 1 || !isLastSection
              }
            />
          ))}
        </div>
      </div>
    );
  };

  const creditsMeta =
    maxLivePreviewCredits === -1 ? (
      <>
        Usage is managed on your account page{' '}
        <a
          href="/account"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-blue hover:underline">
          (view usage)
        </a>
      </>
    ) : (
      <>
        1 credit per client · {remainingCredits} remaining{' '}
        <a
          href="/account"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-blue hover:underline">
          (view usage)
        </a>
      </>
    );

  return (
    <>
    <Modal
      toggle={toggle}
      isOpen={isOpen}
      size={isFreePlan ? 'sm:max-w-2xl' : 'sm:max-w-xl'}
      closeButton
      closeOnEsc={!showPlansModal}>
      {isFreePlan ? (
        <div className="text-left">
          <div className="mb-4 sm:flex sm:items-start">
            <h3
              className="text-base font-semibold tracking-tight text-neutral-900"
              id="modal-headline">
              Upgrade to unlock Live Previews
            </h3>
          </div>
          <P>
            See how your email renders across Gmail, Apple Mail, Outlook, and more
            before you send. Live preview credits aren&apos;t included on the Free
            plan.
          </P>
          <GmailFramePreview
            html={data?.rawTemplate}
            css={data?.css}
            title={data?.name}
          />
          <div className="mt-6 mb-1 flex items-center gap-2">
            <Button tag="button" type="button" onClick={onUpgradeClick}>
              View plans
            </Button>
            <button
              type="button"
              onClick={toggle}
              className="px-1 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-800">
              Not right now
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-full max-h-[calc(100vh-5rem)] min-h-0 flex-col text-left">
          <div className="flex-none border-b border-subtle pb-3 pr-8">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0">
                <h3
                  className="text-base font-semibold tracking-tight text-neutral-900"
                  id="modal-headline">
                  Live Client Previews
                </h3>
                <p className="mt-1 text-xs text-neutral-500">{creditsMeta}</p>
              </div>
              <div className="flex flex-none items-center gap-2 pt-0.5 text-xs">
                <span className="font-medium text-neutral-800">
                  {selectedCount} selected
                </span>
                <span className="text-neutral-300">·</span>
                <button
                  type="button"
                  className="font-medium text-blue hover:text-blue-dark"
                  onClick={selectAllClients}>
                  Select all
                </button>
                <span className="text-neutral-300">·</span>
                <button
                  type="button"
                  className="font-medium text-neutral-500 hover:text-neutral-700"
                  onClick={clearSelectedClients}>
                  None
                </button>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {formError && (
              <div className="px-1 pt-3">
                <ErrorBanner title={formError} />
              </div>
            )}

            {exceedsCredits && (
              <div className="px-1 pt-3">
                <ErrorBanner
                  title={`You selected ${selectedCount} clients but only have ${remainingCredits} live preview credits remaining.`}
                />
              </div>
            )}

            <div>
              {PREVIEW_CLIENT_SECTIONS.map((section, index) =>
                renderCategorySection(
                  section,
                  index === PREVIEW_CLIENT_SECTIONS.length - 1
                )
              )}
            </div>
          </div>

          <div className="mt-6 mb-1 flex flex-none gap-2">
            <Button tag="button" onClick={onGenerate} disabled={!canGenerate}>
              {isSubmitting ? 'Generating…' : 'Generate Previews'}
            </Button>
            <Button tag="button" outline onClick={toggle}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
    <PlansModal
      isOpen={showPlansModal}
      toggle={closePlansAndReturnToEditor}
    />
    </>
  );
}

export default LiveClientPreviewsModal;
